import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@/api/client";
import {
  getElementsMetrics,
  getProjectAssemblyElements,
  getProjectMetricValues,
  listEquipmentTypes,
  putProjectAssemblyElements,
  putProjectMetricValues,
  resolveTypeElements,
  type AssemblyElement,
  type EquipmentType,
  type MetricValueInput,
  type MetricWithOptions,
  type ProjectMetricValue,
  type TypeElementsResolution,
} from "@/api/equipment";

export const EQUIPMENT_TYPES_KEY = ["equipment-types"] as const;
export const TYPE_ELEMENTS_KEY = (typeId: number) => ["type-elements", typeId] as const;
export const PROJECT_ASSEMBLY_ELEMENTS_KEY = (projectId: number) =>
  ["project-assembly-elements", projectId] as const;
export const PROJECT_METRIC_VALUES_KEY = (projectId: number) =>
  ["project-metric-values", projectId] as const;

function noRetryOn401(failureCount: number, error: Error): boolean {
  if (error instanceof ApiError && error.status === 401) return false;
  return failureCount < 1;
}

export function useEquipmentTypes(enabled = true) {
  return useQuery<EquipmentType[], Error>({
    queryKey: EQUIPMENT_TYPES_KEY,
    queryFn: listEquipmentTypes,
    enabled,
    retry: noRetryOn401,
    staleTime: 60_000,
  });
}

export function useTypeElements(typeId: number | null) {
  return useQuery<TypeElementsResolution, Error>({
    queryKey: TYPE_ELEMENTS_KEY(typeId ?? 0),
    queryFn: () => resolveTypeElements(typeId as number),
    enabled: typeId != null,
    retry: noRetryOn401,
    staleTime: 60_000,
  });
}

export function useProjectAssemblyElements(projectId: number, enabled = true) {
  return useQuery<AssemblyElement[], Error>({
    queryKey: PROJECT_ASSEMBLY_ELEMENTS_KEY(projectId),
    queryFn: () => getProjectAssemblyElements(projectId),
    enabled,
    retry: noRetryOn401,
    staleTime: 30_000,
  });
}

export interface PutAssemblyElementsVars {
  projectId: number;
  elementIds: number[];
}

export function usePutProjectAssemblyElements() {
  const qc = useQueryClient();
  return useMutation<AssemblyElement[], Error, PutAssemblyElementsVars>({
    mutationFn: ({ projectId, elementIds }) => putProjectAssemblyElements(projectId, elementIds),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: PROJECT_ASSEMBLY_ELEMENTS_KEY(vars.projectId) });
      // Опросник метрик (`useElementsMetrics`) кэшируется по составу элементов —
      // при смене состава ключ меняется сам, отдельно сбрасывать не нужно.
    },
  });
}

/**
 * Каталог метрик по «живому» набору элементов — опросник в drawer'е до
 * сохранения (§3.3). queryKey сортирует id для стабильности кэша;
 * `keepPreviousData` держит прежний опросник на экране, пока догружается
 * новый набор, — без мигания при правке состава.
 */
export function useElementsMetrics(ids: number[], enabled = true) {
  const sorted = [...ids].sort((a, b) => a - b);
  return useQuery<MetricWithOptions[], Error>({
    queryKey: ["elements-metrics", sorted],
    queryFn: () => getElementsMetrics(sorted),
    enabled: enabled && ids.length > 0,
    retry: noRetryOn401,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

/** Сохранённые значения метрик проекта (§3.3). */
export function useProjectMetricValues(projectId: number, enabled = true) {
  return useQuery<ProjectMetricValue[], Error>({
    queryKey: PROJECT_METRIC_VALUES_KEY(projectId),
    queryFn: () => getProjectMetricValues(projectId),
    enabled,
    retry: noRetryOn401,
    staleTime: 30_000,
  });
}

export interface PutMetricValuesVars {
  projectId: number;
  values: MetricValueInput[];
}

export function usePutProjectMetricValues() {
  const qc = useQueryClient();
  return useMutation<ProjectMetricValue[], Error, PutMetricValuesVars>({
    mutationFn: ({ projectId, values }) => putProjectMetricValues(projectId, values),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: PROJECT_METRIC_VALUES_KEY(vars.projectId) });
    },
  });
}
